import {useContext, useEffect, useState} from 'preact/hooks';
import {useLocation} from 'preact-iso';

import {
  createEmptyGroup,
  createItem,
  deleteGroupDraft,
  loadAdminState,
  loadProducts,
  publishGroup,
  saveGroupDraft,
} from '../lib/api.js';
import {LanguageContext, useTranslate} from '../lib/i18n.js';

export default function GroupPage({id}) {
  const t = useTranslate();
  const {toggleLanguage} = useContext(LanguageContext);
  const {route} = useLocation();
  const isNew = id === 'new';
  const [group, setGroup] = useState(null);
  const [products, setProducts] = useState(new Map());
  const [openItems, setOpenItems] = useState(new Set());
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);

  useEffect(() => { load(); }, [id]);

  async function load() {
    setStatus('loading');
    setError('');
    try {
      const data = await loadAdminState();
      const nextGroup = isNew ? createEmptyGroup() : data.state.groups.find((candidate) => candidate.id === id);
      if (!nextGroup) {
        setStatus('not-found');
        return;
      }
      setGroup(clone(nextGroup));
      const productIds = [...nextGroup.assignments, ...nextGroup.items.map((item) => item.productGid)];
      const loadedProducts = await loadProducts(productIds);
      setProducts(new Map(loadedProducts.map((product) => [product.id, product])));
      setStatus('idle');
    } catch (loadError) {
      setError(message(loadError));
      setStatus('idle');
    }
  }

  function change(patch) {
    setGroup((current) => ({...current, ...patch, status: 'DRAFT'}));
  }

  function changeItem(itemId, patch) {
    setGroup((current) => ({
      ...current,
      status: 'DRAFT',
      items: current.items.map((item) => item.id === itemId ? {...item, ...patch} : item),
    }));
  }

  async function pickMainProducts() {
    const selected = await /** @type {any} */ (shopify).resourcePicker({
      type: 'product',
      multiple: true,
      selectionIds: group.assignments.map((productId) => ({id: productId})),
      filter: {variants: false, archived: false},
    });
    if (selected === undefined) return;
    mergeProducts(selected);
    change({assignments: selected.map((product) => product.id)});
  }

  async function pickAccessories() {
    const selected = await /** @type {any} */ (shopify).resourcePicker({
      type: 'product',
      multiple: 10,
      selectionIds: group.items.map((item) => ({id: item.productGid})),
      filter: {variants: true, archived: false},
    });
    if (selected === undefined) return;
    if (selected.length > 10) {
      shopify.toast.show(t('maxItems'));
      return;
    }
    mergeProducts(selected);
    const existing = new Map(group.items.map((item) => [item.productGid, item]));
    const selectedIds = new Set(selected.map((product) => product.id));
    const keptInCurrentOrder = group.items.filter((item) => selectedIds.has(item.productGid));
    const keptIds = new Set(keptInCurrentOrder.map((item) => item.productGid));
    const added = selected
      .filter((product) => !keptIds.has(product.id))
      .map((product) => existing.get(product.id) || createItem(product.id));
    change({items: [...keptInCurrentOrder, ...added]});
  }

  function mergeProducts(selected) {
    setProducts((current) => {
      const next = new Map(current);
      selected.forEach((product) => next.set(product.id, normalizePickerProduct(product)));
      return next;
    });
  }

  async function save() {
    const validation = validate(group, t);
    if (validation) {
      setError(validation);
      return;
    }
    setStatus('saving');
    setError('');
    try {
      const result = await saveGroupDraft(group);
      setGroup(clone(result.group));
      shopify.toast.show(t('saved'));
      if (isNew) route(`/groups/${result.group.id}`);
    } catch (saveError) {
      setError(message(saveError));
    } finally {
      setStatus('idle');
    }
  }

  async function publish() {
    const validation = validate(group, t);
    if (validation) {
      setError(validation);
      return;
    }
    setStatus('publishing');
    setError('');
    try {
      const published = await publishGroup(clone(group), products);
      setGroup(clone(published));
      shopify.toast.show(t('publishedOk'));
      if (isNew) route(`/groups/${published.id}`);
    } catch (publishError) {
      setError(message(publishError));
    } finally {
      setStatus('idle');
    }
  }

  async function removeGroup() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      shopify.toast.show(t('confirmDelete'));
      return;
    }
    setStatus('deleting');
    setError('');
    try {
      await deleteGroupDraft(group.id);
      shopify.toast.show(t('deleted'));
      route('/');
    } catch (deleteError) {
      setError(message(deleteError));
      setStatus('idle');
    }
  }

  function removeItem(itemId) {
    change({items: group.items.filter((item) => item.id !== itemId)});
    setOpenItems((current) => {
      const next = new Set(current);
      next.delete(itemId);
      return next;
    });
  }

  function moveItem(index, offset) {
    moveItemTo(index, index + offset);
  }

  function moveItemTo(index, target) {
    if (target < 0 || target >= group.items.length) return;
    const next = [...group.items];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    change({items: next});
  }

  function finishDrag(targetItemId) {
    const source = group.items.findIndex((item) => item.id === draggedItemId);
    const target = group.items.findIndex((item) => item.id === targetItemId);
    if (source >= 0 && target >= 0 && source !== target) moveItemTo(source, target);
    setDraggedItemId(null);
    setDragOverItemId(null);
  }

  function toggleItem(itemId) {
    setOpenItems((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  const heading = group?.name || (isNew ? t('createGroup') : t('groups'));
  const busy = ['saving', 'publishing', 'deleting'].includes(status);

  if (status === 'not-found') {
    return <s-page heading={t('loadFailed')}><s-link slot="breadcrumb-actions" href="/">{t('back')}</s-link></s-page>;
  }

  return (
    <s-page heading={heading}>
      <s-link slot="breadcrumb-actions" href="/">{t('back')}</s-link>
      <s-button slot="secondary-actions" onClick={toggleLanguage}>{t('language')}</s-button>
      {!isNew && (
        <s-button slot="secondary-actions" tone="critical" loading={status === 'deleting'} onClick={removeGroup}>
          {confirmDelete ? t('confirmDelete') : t('deleteGroup')}
        </s-button>
      )}
      <s-button slot="secondary-actions" loading={status === 'publishing'} disabled={busy} onClick={publish}>
        {t('publish')}
      </s-button>
      <s-button slot="primary-action" variant="primary" loading={status === 'saving'} disabled={busy} onClick={save}>
        {t('saveDraft')}
      </s-button>

      {error && <s-banner tone="critical">{friendlyError(error, t)}</s-banner>}
      {status === 'loading' && <s-section><s-paragraph>{t('loading')}</s-paragraph></s-section>}

      {group && status !== 'loading' && (
        <>
          <s-section heading={t('groupName')}>
            <s-stack gap="base">
              <s-text-field label={t('groupName')} value={group.name} required onInput={(event) => change({name: inputValue(event)})} />
              <s-switch label={t('enabled')} checked={group.enabled} onChange={(event) => change({enabled: checkedValue(event)})} />
              <s-stack direction="inline" gap="small-200">
                <s-text type="strong">{t('status')}:</s-text>
                <s-badge tone={group.status === 'PUBLISHED' ? 'success' : 'info'}>
                  {group.status === 'PUBLISHED' ? t('published') : t('draft')}
                </s-badge>
              </s-stack>
            </s-stack>
          </s-section>

          <s-section heading={t('mainProducts')}>
            <s-stack gap="base">
              <s-paragraph>{t('mainProductsHelp')}</s-paragraph>
              <s-button onClick={pickMainProducts}>{t('selectMainProducts')}</s-button>
              <ProductList
                ids={group.assignments}
                products={products}
                t={t}
                onRemove={(productId) => change({assignments: group.assignments.filter((id) => id !== productId)})}
              />
            </s-stack>
          </s-section>

          <s-section heading={`${t('accessories')} (${group.items.length}/10)`}>
            <s-stack gap="base">
              <s-paragraph>{t('accessoriesHelp')}</s-paragraph>
              <s-button onClick={pickAccessories} disabled={group.items.length >= 10}>{t('addAccessories')}</s-button>
              {group.items.map((item, index) => (
                <AccessoryEditor
                  key={item.id}
                  item={item}
                  index={index}
                  count={group.items.length}
                  product={products.get(item.productGid)}
                  open={openItems.has(item.id)}
                  t={t}
                  onToggle={() => toggleItem(item.id)}
                  onChange={(patch) => changeItem(item.id, patch)}
                  onRemove={() => removeItem(item.id)}
                  onMove={(offset) => moveItem(index, offset)}
                  onMoveTo={(target) => moveItemTo(index, target)}
                  dragging={draggedItemId === item.id}
                  dragOver={dragOverItemId === item.id && draggedItemId !== item.id}
                  onDragStart={() => setDraggedItemId(item.id)}
                  onDragOver={() => setDragOverItemId(item.id)}
                  onDrop={() => finishDrag(item.id)}
                  onDragEnd={() => {
                    setDraggedItemId(null);
                    setDragOverItemId(null);
                  }}
                />
              ))}
            </s-stack>
          </s-section>

          <s-section>
            <s-button-group>
              <s-button variant="primary" loading={status === 'saving'} disabled={busy} onClick={save}>{t('saveDraft')}</s-button>
              <s-button loading={status === 'publishing'} disabled={busy} onClick={publish}>{t('publish')}</s-button>
            </s-button-group>
          </s-section>
        </>
      )}
    </s-page>
  );
}

function ProductList({ids, products, t, onRemove}) {
  if (!ids.length) return null;
  return (
    <s-stack gap="small-200">
      {ids.map((productId) => {
        const product = products.get(productId);
        return (
          <s-box key={productId} border="base" borderRadius="base" padding="base">
            <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
              <s-stack direction="inline" gap="base" alignItems="center">
                {product?.featuredImage?.url && <s-thumbnail src={product.featuredImage.url} alt={product.featuredImage.altText || product.title} size="small" />}
                <s-stack gap="small-100">
                  <s-text type="strong">{product?.title || t('productUnavailable')}</s-text>
                  {product?.vendor && <s-text color="subdued">{product.vendor}</s-text>}
                </s-stack>
              </s-stack>
              <s-button tone="critical" variant="tertiary" onClick={() => onRemove(productId)}>{t('removeMain')}</s-button>
            </s-stack>
          </s-box>
        );
      })}
    </s-stack>
  );
}

function AccessoryEditor({
  item, index, count, product, open, t, onToggle, onChange, onRemove,
  onMove, onMoveTo, dragging, dragOver, onDragStart, onDragOver, onDrop, onDragEnd,
}) {
  const variants = product?.variants?.nodes || product?.variants || [];
  const editor = (
    <s-box border="base" borderRadius="base" padding="base">
      <s-stack gap="base">
        <s-stack direction="inline" alignItems="center" justifyContent="space-between" gap="small-200">
          <s-button variant="tertiary" onClick={onToggle}>
            {open ? '▼' : '▶'} {index + 1}. {item.customTitle || product?.title || t('productUnavailable')}
          </s-button>
          <s-stack direction="inline" gap="small-100" alignItems="center">
            {isIframeDom() ? (
              <span className="impact-addon-drag-hint" aria-label={t('movePosition')}>⠿</span>
            ) : (
              <s-select
                label={t('movePosition')}
                labelAccessibilityVisibility="exclusive"
                icon="drag-handle"
                value={String(index)}
                onChange={(event) => onMoveTo(Number(inputValue(event)))}
              >
                {Array.from({length: count}, (_, position) => (
                  <s-option key={position} value={String(position)}>{t('position')} {position + 1}</s-option>
                ))}
              </s-select>
            )}
            <s-button accessibilityLabel={t('moveUp')} disabled={index === 0} onClick={() => onMove(-1)}>↑</s-button>
            <s-button accessibilityLabel={t('moveDown')} disabled={index === count - 1} onClick={() => onMove(1)}>↓</s-button>
            <s-button tone="critical" variant="tertiary" onClick={onRemove}>{t('removeOutside')}</s-button>
          </s-stack>
        </s-stack>
        <s-text color="subdued">{product?.vendor || ''} · {variants.length} SKU</s-text>

        {open && (
          <s-stack gap="base">
            <s-text-field label={t('title')} value={item.customTitle} onInput={(event) => onChange({customTitle: inputValue(event)})} />
            <s-text-area label={t('description')} value={item.shortDescription} rows={3} onInput={(event) => onChange({shortDescription: inputValue(event)})} />
            <s-select label={t('variantMode')} value={item.variantMode} onChange={(event) => onChange({variantMode: inputValue(event)})}>
              <s-option value="auto">{t('variantAuto')}</s-option>
              <s-option value="selector">{t('variantSelector')}</s-option>
              <s-option value="fixed">{t('variantFixed')}</s-option>
            </s-select>
            {item.variantMode === 'fixed' && (
              <VariantSelect label={t('fixedVariant')} value={item.fixedVariantGid} variants={variants} t={t} onChange={(value) => onChange({fixedVariantGid: value})} />
            )}
            {item.variantMode !== 'fixed' && variants.length > 1 && (
              <VariantSelect label={t('defaultVariant')} value={item.defaultVariantGid} variants={variants} t={t} onChange={(value) => onChange({defaultVariantGid: value})} />
            )}
            <s-select label={t('quantityMode')} value={item.quantityMode} onChange={(event) => onChange({quantityMode: inputValue(event)})}>
              <s-option value="one">{t('quantityOne')}</s-option>
              <s-option value="customer">{t('quantityCustomer')}</s-option>
            </s-select>
            <s-switch label={t('defaultSelected')} checked={item.defaultSelected} onChange={(event) => onChange({defaultSelected: checkedValue(event)})} />
            <s-switch label={t('itemEnabled')} checked={item.enabled} onChange={(event) => onChange({enabled: checkedValue(event)})} />
          </s-stack>
        )}
      </s-stack>
    </s-box>
  );

  if (!isIframeDom()) return editor;

  return (
    <div
      className={`impact-addon-sort-card${dragging ? ' is-dragging' : ''}${dragOver ? ' is-drag-over' : ''}`}
      draggable="true"
      data-item-id={item.id}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', item.id);
        onDragStart();
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        onDragOver();
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop();
      }}
      onDragEnd={onDragEnd}
    >
      {editor}
    </div>
  );
}

function isIframeDom() {
  return typeof window !== 'undefined' && window.__IMPACT_ADDONS_IFRAME__ === true;
}

function VariantSelect({label, value, variants, t, onChange}) {
  return (
    <s-select label={label} value={value || ''} onChange={(event) => onChange(inputValue(event))}>
      <s-option value="">{t('choose')}</s-option>
      {variants.map((variant) => (
        <s-option key={variant.id} value={variant.id}>{variant.title} · {variant.price}</s-option>
      ))}
    </s-select>
  );
}

function validate(group, t) {
  if (!group.name.trim()) return t('requiredName');
  if (!group.assignments.length) return t('requiredAssignments');
  if (!group.items.length) return t('requiredItems');
  if (group.items.length > 10) return t('maxItems');
  if (group.items.some((item) => item.enabled && item.variantMode === 'fixed' && !item.fixedVariantGid)) return t('fixedRequired');
  return '';
}

function friendlyError(error, t) {
  if (error.startsWith('ASSIGNMENT_CONFLICT:')) return `${t('assignmentConflict')}: ${error.split(':').slice(1).join(':').trim()}`;
  const errors = {
    GROUP_NAME_REQUIRED: t('requiredName'), ASSIGNMENTS_REQUIRED: t('requiredAssignments'),
    ADDON_ITEMS_REQUIRED: t('requiredItems'), ADDON_ITEM_LIMIT: t('maxItems'), FIXED_VARIANT_REQUIRED: t('fixedRequired'),
  };
  return errors[error] || error;
}

function normalizePickerProduct(product) {
  const variants = Array.isArray(product.variants) ? product.variants : product.variants?.nodes || [];
  const images = Array.isArray(product.images) ? product.images : product.images?.nodes || [];
  const pickerImage = images[0];
  const featuredImage = product.featuredImage || (pickerImage ? {
    url: pickerImage.url || pickerImage.originalSrc || '',
    altText: pickerImage.altText || product.title || '',
  } : null);
  return {...product, featuredImage, variants: {nodes: variants}};
}

function inputValue(event) {
  return event?.target && 'value' in event.target ? String(event.target.value) : '';
}

function checkedValue(event) {
  return Boolean(event?.target && 'checked' in event.target ? event.target.checked : false);
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function message(error) {
  return error instanceof Error ? error.message : String(error);
}
